export interface ParsedHandoffContext {
  briefReason: string;
  messageCount: number;
  conversationTurns: Array<{ role: 'customer' | 'ai'; text: string }>;
  rawText: string;
}

/**
 * Parse the raw handoffReason string into structured data.
 *
 * Input format:
 *   "Voice call from customer (Session: <uuid>) Message count: N
 *    Duration: Since ... Recent conversation: Customer: ... AI: ..."
 */
export function parseHandoffReason(
  raw: string | undefined,
  aiSummaryKeyIssue?: string,
): ParsedHandoffContext {
  const fallback: ParsedHandoffContext = {
    briefReason: 'Customer requested human agent',
    messageCount: 0,
    conversationTurns: [],
    rawText: raw ?? '',
  };

  if (!raw) return fallback;

  // Extract message count
  const countMatch = raw.match(/Message count:\s*(\d+)/i);
  const messageCount = countMatch ? parseInt(countMatch[1], 10) : 0;

  // Extract conversation turns from "Recent conversation:" section
  const conversationTurns: Array<{ role: 'customer' | 'ai'; text: string }> = [];
  const recentIdx = raw.indexOf('Recent conversation:');

  if (recentIdx !== -1) {
    const conversationPart = raw.slice(recentIdx + 'Recent conversation:'.length).trim();

    // Split by "Customer:" and "AI:" markers
    const turnRegex = /(?:^|\s)(Customer|AI):\s*/gi;
    const parts: Array<{ role: 'customer' | 'ai'; start: number }> = [];
    let match: RegExpExecArray | null;

    while ((match = turnRegex.exec(conversationPart)) !== null) {
      parts.push({
        role: match[1].toLowerCase() === 'customer' ? 'customer' : 'ai',
        start: match.index + match[0].length,
      });
    }

    for (let i = 0; i < parts.length; i++) {
      const end = i + 1 < parts.length ? parts[i + 1].start - parts[i + 1].role.length - 2 : conversationPart.length;
      const text = conversationPart.slice(parts[i].start, end).replace(/\.\.\.\s*$/, '').trim();
      if (text) {
        conversationTurns.push({ role: parts[i].role, text });
      }
    }
  }

  // Determine brief reason
  let briefReason = 'Customer requested human agent';

  if (aiSummaryKeyIssue) {
    briefReason = aiSummaryKeyIssue;
  } else if (conversationTurns.length > 0) {
    // Use last customer message as brief reason
    const lastCustomerTurn = [...conversationTurns].reverse().find((t) => t.role === 'customer');
    if (lastCustomerTurn) {
      briefReason = lastCustomerTurn.text.length > 80
        ? lastCustomerTurn.text.slice(0, 77) + '...'
        : lastCustomerTurn.text;
    }
  }

  return {
    briefReason,
    messageCount,
    conversationTurns,
    rawText: raw,
  };
}

/**
 * Map sentiment/mood string to a color scheme for UI display.
 */
export function getSentimentColor(mood: string | undefined): {
  bg: string;
  text: string;
  label: string;
} {
  const m = (mood ?? 'neutral').toLowerCase();

  switch (m) {
    case 'frustrated':
    case 'angry':
    case 'negative':
      return { bg: 'bg-red-100', text: 'text-red-700', label: m };
    case 'concerned':
    case 'anxious':
    case 'mixed':
      return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: m };
    case 'positive':
    case 'happy':
    case 'satisfied':
      return { bg: 'bg-green-100', text: 'text-green-700', label: m };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-600', label: m || 'neutral' };
  }
}